import { Check, Loader2, Pencil, Plus, Tag as TagIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button.js';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover.js';
import { useTags } from '../../common/admin-hooks.js';
import { useUpdateScenario } from '../hooks.js';
import { TagChip } from './TagChip.js';
import { TagEditorDialog } from './TagEditorDialog.js';

/**
 * In-place tag editor for the scenario detail modal. Mirrors the
 * legacy `wf-audit/src/slices/presentation/components/tagSelector/Provider.tsx`
 * + `presentationThumb/TagSelector.tsx` flow:
 *
 *   1. Click the "Edit tags" trigger →
 *   2. Popover lists every tag in the license; checkboxes pre-filled
 *      from the scenario's current attached set.
 *   3. Optional inline "Create new tag" field — title only; colour
 *      defaults to a neutral grey (operator can refine on the admin
 *      tag page).
 *   4. Save calls `PUT /v1/scenarios/{id}` with `{tagIds: [...]}`,
 *      which the engine maps to a transactional DELETE+INSERT on
 *      `fusion_scenario__scenario_tag`. Cancel discards the local
 *      checkbox state.
 *
 * Render-only when the modal has a real `fusionId` (we never write
 * tags against a placeholder).
 */
export interface ScenarioTagSelectorProps {
  fusionId: string;
  /** Currently-attached tag ids (from the scenario detail row). */
  currentTagIds: ReadonlyArray<number>;
  /**
   * Optional override for the popover trigger element. The detail
   * modal + presentation rail want the default "Pencil + Edit"
   * button; the Value Metrics table wants a bare tag-icon button
   * sized to fit a row cell (legacy parity). Anything passed here
   * replaces the default trigger entirely — Popover/Radix injects
   * the ref + click handler via `asChild`.
   */
  trigger?: ReactNode;
}

interface TagOption {
  id: number;
  title: string;
  description: string | null;
  color: string | null;
  icon: string | null;
}

export function ScenarioTagSelector({
  fusionId,
  currentTagIds,
  trigger,
}: ScenarioTagSelectorProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const { data: tagRows = [], isLoading } = useTags();
  const { update, isPending: saving } = useUpdateScenario(fusionId);

  // Tag editor dialog state — either `null` (closed), `'create'`
  // (open in new-tag mode), or a tag option (open in edit mode for
  // that tag). Single piece of state keeps the create/edit branches
  // cleanly mutually exclusive.
  const [editorTarget, setEditorTarget] = useState<TagOption | 'create' | null>(null);

  const allTags = useMemo<ReadonlyArray<TagOption>>(
    () =>
      tagRows.flatMap((r) => {
        if (!r || typeof r !== 'object') return [];
        const id = typeof r.id === 'number' ? r.id : null;
        const title = typeof r.title === 'string' ? r.title : null;
        if (id === null || title === null) return [];
        return [
          {
            id,
            title,
            description: typeof r.description === 'string' ? r.description : null,
            color: typeof r.color === 'string' ? r.color : null,
            icon: typeof r.icon === 'string' ? r.icon : null,
          },
        ];
      }),
    [tagRows],
  );

  // Local checkbox state. Re-seed from `currentTagIds` each time the
  // popover opens so reopening discards any unsaved edits.
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set(currentTagIds));
  useEffect(() => {
    if (open) setSelected(new Set(currentTagIds));
  }, [open, currentTagIds]);

  function toggle(id: number): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSave(): Promise<void> {
    const next = [...selected].sort((a, b) => a - b);
    const prev = [...currentTagIds].sort((a, b) => a - b);
    // Skip the round-trip if nothing changed — common for an
    // accidental click on the Edit button.
    const unchanged = prev.length === next.length && prev.every((id, i) => id === next[i]);
    if (unchanged) {
      setOpen(false);
      return;
    }
    try {
      await update({ tagIds: next });
      setOpen(false);
    } catch {
      // useUpdateScenario surfaces the error via toast; popover stays
      // open so the operator can retry.
    }
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {trigger ?? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Edit tags"
              title="Edit tags"
              className="h-7 gap-1.5 px-2 text-xs text-foreground/70 hover:bg-foreground/10 hover:text-foreground"
            >
              <Pencil className="h-3 w-3" />
              Edit
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-72 border-foreground/15 bg-[color:var(--surface-overlay)] p-3 text-foreground"
        >
          <header className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-foreground/55">
            <TagIcon className="h-3.5 w-3.5" /> Tags
          </header>

          {isLoading ? (
            <div className="flex items-center gap-2 px-1 py-3 text-xs text-foreground/55">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading tags…
            </div>
          ) : allTags.length === 0 ? (
            <p className="px-1 py-2 text-xs text-foreground/50">No tags yet — create one below.</p>
          ) : (
            <ul className="max-h-56 space-y-0.5 overflow-y-auto">
              {allTags.map((t) => {
                const checked = selected.has(t.id);
                return (
                  <li key={t.id} className="group flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => toggle(t.id)}
                      role="menuitemcheckbox"
                      aria-checked={checked}
                      className="flex flex-1 items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors hover:bg-foreground/10"
                    >
                      <span
                        className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-foreground/30"
                        style={{
                          background: checked
                            ? t.color
                              ? t.color
                              : 'var(--brand-yellow)'
                            : 'transparent',
                        }}
                      >
                        {checked ? (
                          <Check className="h-3 w-3" style={{ color: 'var(--gradient-end)' }} />
                        ) : null}
                      </span>
                      {/* Use the shared `TagChip` for the option label
                          so popover rows match the rendered chip on
                          the scenario row / detail modal / presentation
                          page exactly (same colour, same icon glyph). */}
                      <TagChip tag={t} size="sm" className="flex-1" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditorTarget(t)}
                      aria-label={`Edit tag ${t.title}`}
                      title="Edit tag"
                      className="rounded p-1 text-foreground/40 opacity-0 transition-opacity hover:bg-foreground/10 hover:text-foreground group-hover:opacity-100"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* "Create new tag" opens the rich TagEditorDialog with the
            full field set (title + description + color + icon).
            Operator-reported: "add new tag or edit tag has more data
            in legacy system, like description, color, icon, please
            do a proper popup for tags management". */}
          <div className="mt-2 border-t border-foreground/10 pt-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setEditorTarget('create')}
              className="h-7 w-full justify-start gap-1.5 px-2 text-xs text-foreground/75 hover:bg-foreground/10 hover:text-foreground"
            >
              <Plus className="h-3.5 w-3.5" />
              Create new tag
            </Button>
          </div>

          <div className="mt-3 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={saving}
              className="h-7 px-2 text-xs text-foreground/70 hover:bg-foreground/10 hover:text-foreground"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => void handleSave()}
              disabled={saving}
              className="h-7 bg-[color:var(--brand-yellow)] px-3 text-xs text-[color:var(--gradient-end)] hover:opacity-90"
            >
              {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              Save
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <TagEditorDialog
        open={editorTarget !== null}
        onOpenChange={(next) => {
          if (!next) setEditorTarget(null);
        }}
        initial={
          editorTarget && editorTarget !== 'create'
            ? {
                id: editorTarget.id,
                title: editorTarget.title,
                description: editorTarget.description,
                color: editorTarget.color,
                icon: editorTarget.icon,
              }
            : null
        }
        onSaved={(newId) => {
          // For freshly-created tags, auto-select so the operator's
          // next Save in the popover attaches the new tag too.
          if (editorTarget === 'create') {
            setSelected((prev) => new Set([...prev, newId]));
          }
          setEditorTarget(null);
        }}
      />
    </>
  );
}
